import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { buildTierItems, addPackItems } from '@theme/pack-tiers';
import {
  readSizeMatrix,
  cellFor,
  reservedUnitsBySize,
  availabilityFor,
  parseGiftSizeMap,
  NO_SIZE_KEY,
} from '@theme/pack-sizes';
import { formatMoney } from '@theme/money-formatting';
import { fetchCartJson } from '@theme/utilities';

/**
 * Se emite cuando cambia el pack elegido. Lo escucha `assets/sticky-add-to-cart.js`, que muestra
 * el mismo total en la barra flotante.
 *
 * OJO: este string esta DUPLICADO en `assets/sticky-add-to-cart.js`. Si se toca, hay que tocar
 * los dos. Se prefirio la copia antes que exportarlo e importarlo porque la dependencia queda al
 * reves: este modulo carga solo en productos con packs, mientras que la barra flotante carga en
 * TODA ficha de producto — importarlo desde alla arrastraria este archivo (y `pack-tiers.js`
 * detras) a paginas que no los necesitan. Un string de 16 caracteres no vale ese costo.
 */
export const PACK_TIER_CHANGE = 'pack-tier:change';

const ERROR_MESSAGE_DISPLAY_DURATION = 6000;
const SUCCESS_MESSAGE_DISPLAY_DURATION = 4000;
const ADDED_BUTTON_TEXT_DURATION = 2000;

/**
 * Renders selectable purchase "packs" (quantity tiers priced by an automatic discount, with
 * optional free gifts) and adds every resulting line (main item + gifts) to the cart in a
 * single batch request, mirroring the multi-item add pattern used by ProductFormComponent
 * (`@theme/product-form`) for variant-change queue draining.
 *
 * @typedef {object} PurchaseOptionsRefs
 * @property {HTMLInputElement[]} tierInputs
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} addToCartButtonText
 * @property {HTMLElement | undefined} addToCartError
 * @property {HTMLElement} liveRegion
 * @property {HTMLElement | undefined} summary
 * @property {HTMLElement | undefined} summaryLabel
 * @property {HTMLElement | undefined} summaryTotal
 * @property {HTMLElement | undefined} summaryCompare
 * @property {HTMLElement | undefined} summarySave
 * @property {HTMLElement | undefined} summaryTransfer
 * @property {HTMLElement | undefined} summaryShipping
 * @property {HTMLElement | undefined} summaryTransferShipping
 * @property {HTMLElement | undefined} summaryInstallments
 * @property {HTMLElement | undefined} shippingCost
 * @property {HTMLElement | undefined} shippingFree
 * @extends Component<PurchaseOptionsRefs>
 */
export class PurchaseOptionsComponent extends Component {
  // Los refs del resumen quedan DELIBERADAMENTE fuera de esta lista. `requiredRefs` lanza en
  // `connectedCallback` —antes de que corra una sola línea propia— si falta alguno, y el
  // componente queda montado en el DOM pero inerte: el botón de agregar al carrito dejaría de
  // funcionar por culpa de una pieza decorativa. Se tratan como opcionales más abajo.
  requiredRefs = ['tierInputs', 'addToCartButton', 'addToCartButtonText', 'liveRegion'];

  #abortController = new AbortController();

  /**
   * Lo que ya hay en el carrito, en centavos. Se siembra desde Liquid en el primer pintado y se
   * refresca sola cada vez que el carrito cambia.
   * @type {number}
   */
  #cartTotalCents = 0;

  /**
   * La matriz de tiers × talles, o `null` en un producto sin eje de talle — que es el caso de
   * todo lo que hoy está vendiendo. Todo el código de talles cuelga de que esto no sea `null`.
   * @type {import('@theme/pack-sizes').PackSizeMatrix | null}
   */
  #matrix = null;

  /**
   * El talle elegido, o `null` mientras no se eligió ninguno. `null` NO es "el primero": la
   * elección es obligatoria justamente para que nadie se lleve un talle que no eligió.
   * @type {string | null}
   */
  #selectedSize = null;

  /**
   * Unidades de cada talle que el carrito ya se está llevando. Se refresca sola en cada cambio
   * de carrito; sin esto, el stock que pintó Liquid ignora lo que el cliente ya agregó.
   * @type {Map<string, number>}
   */
  #reservedBySize = new Map();
  /** Lo comprometido en el carrito por CADA producto que surte a un regalo de talle propio.
   * @type {Map<string, Map<string, number>>} */
  #reservedByGiftHandle = new Map();

  /** Cuándo se releyó el stock del servidor por última vez, para no pedirlo en cada alt-tab. */
  #lastStockRead = Date.now();

  connectedCallback() {
    super.connectedCallback();

    this.#cartTotalCents = Number(this.dataset.cartTotalCents) || 0;

    this.#matrix = readSizeMatrix(this);
    if (this.#matrix) {
      // Con eje de talle: un `?variant=` en la URL ya dejó uno marcado desde Liquid, y cualquier
      // otro caso arranca sin talle. SIN eje, la única entrada de la matriz queda elegida de
      // entrada — no hay nada que el cliente tenga que decidir, y el resto del componente sigue
      // funcionando igual porque todo lo que mira es "cuál es la celda actual".
      this.#selectedSize = this.#hasSizeAxis()
        ? this.refs.sizeInputs?.find((input) => input.checked)?.value ?? null
        : NO_SIZE_KEY;
      // Primer pintado desde JS: Liquid dibujó los precios de UN talle, y si el cliente llegó con
      // un `?variant=` puede no ser ése. Además el descuento por carrito sólo lo sabe el cliente.
      this.#applySize({ announce: false });
      this.#refreshReserved();

      // Volver a la pestaña después de un rato es el único momento en que el stock pintado por
      // Liquid puede estar de verdad viejo — una ficha abierta en segundo plano durante media
      // hora mientras el producto se agota. Un minuto de umbral evita que un alt-tab dispare una
      // request por cada vez que el cliente mira otra cosa.
      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.visibilityState !== 'visible') return;
          if (Date.now() - this.#lastStockRead < 60_000) return;
          this.#lastStockRead = Date.now();
          this.#refreshStockFromServer();
        },
        { signal: this.#abortController.signal }
      );
    }

    // Mismo evento y misma fuente que `assets/free-shipping-bar.js`: el aviso de la ficha y la
    // barra del carrito tienen que moverse juntos o se contradicen entre sí.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartChange, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * El carrito cambió (desde este mismo bloque, desde el cajón, desde cualquier otro componente):
   * se relee y se rehacen las dos cuentas que dependen de él.
   *
   * **`await event.promise` no es opcional.** Este evento se emite ANTES de que la operación
   * termine y trae una promesa que resuelve recién cuando el servidor ya aplicó el cambio — es el
   * mismo contrato que respeta `assets/component-cart-items.js`. Sin esperarla, `/cart.js`
   * devuelve el total VIEJO y el aviso se queda una acción atrás: medido, agregar un pack de
   * $160.200 con el carrito vacío dejaba el aviso de "Te faltan $11.000" intacto, y recién se
   * corregía en el cambio siguiente. Es el tipo de bug que parece "el JS no escucha el evento"
   * cuando en realidad lo escucha demasiado temprano.
   *
   * No se ignoran los eventos propios (`event.target === this`): agregar un pack desde este mismo
   * selector es justamente uno de los casos que tiene que refrescar el aviso.
   *
   * Se relee el carrito en vez de usar el payload del evento porque el total con descuentos
   * aplicados sólo lo sabe el servidor: un descuento automático por monto puede mover
   * `total_price` sin que cambie ninguna línea. `fetchCartJson` comparte el pedido en vuelo entre
   * todos los que escuchen el mismo evento, así que esto no agrega una request por componente.
   *
   * @param {Event & { promise?: Promise<unknown> }} event
   */
  #handleCartChange = async (event) => {
    /** @type {any} */
    let resolved;
    try {
      resolved = await event?.promise;
    } catch {
      // La operación falló: el carrito no cambió y no hay nada que recalcular.
      return;
    }

    try {
      // El carrito que la operación ya trajo (`detail.cartData`, misma forma que `/cart.js`)
      // ahorra la relectura; sólo se pide de nuevo si el evento vino sin él (una app externa).
      const cart = resolved?.detail?.cartData ?? (await fetchCartJson());
      this.#cartTotalCents = Number(cart?.total_price) || 0;
      // El mismo carrito que se acaba de leer sirve para las dos cuentas: el total para el aviso
      // de envío gratis y las unidades por talle para la disponibilidad. Pedirlo dos veces sería
      // una request de más en el camino de una acción del cliente.
      if (this.#matrix && this.dataset.productHandle) {
        this.#reservedBySize = reservedUnitsBySize(cart, this.dataset.productHandle);
      }
      // Y las de los productos que surten a los regalos de talle propio, del MISMO carrito. Es lo
      // que hace que el desplegable del regalo se apague solo cuando el cliente agota ese talle
      // desde el carrito, sin recargar la ficha.
      this.#reservedByGiftHandle = new Map();
      for (const giftHandle of this.#giftStockHandles()) {
        this.#reservedByGiftHandle.set(giftHandle, reservedUnitsBySize(cart, giftHandle));
      }
    } catch {
      // Un carrito que no se pudo leer deja el último valor conocido. Es preferible un aviso
      // apenas desactualizado a uno que desaparece o que salta a "te faltan $100.000".
      return;
    }

    // Con eje de talle, `#applySize` ya hace el resumen y los avisos al final — llamarlos
    // también acá los correría dos veces por cambio de carrito.
    if (this.#matrix) {
      this.#applySize({ announce: false });
      return;
    }

    this.#updateSummary();
    this.#updateNudges();
  };

  #busy = false;
  /** @type {number | undefined} */
  #errorTimeout;
  /** @type {number | undefined} */
  #addedTimeout;
  /** @type {string | undefined} */
  #defaultButtonText;

  async #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  #getSelectedTierInput() {
    return this.refs.tierInputs.find((input) => input.checked);
  }

  /**
   * Mantiene el resumen (total, tachado, ahorro, transferencia, envío) sincronizado con el pack
   * elegido.
   *
   * Existe porque hasta ahora la selección era CSS puro (`:has(input:checked)`) y nadie
   * escuchaba el cambio: el precio grande de la ficha era el de la variante base y no se movía
   * al elegir otro pack. Medido en vivo el 2026-09-01, el tier preseleccionado costaba $160.200
   * mientras arriba decía $89.000.
   *
   * Liquid ya pinta este bloque con los valores del tier por defecto, así que sin JS el resumen
   * igual es correcto. Esto sólo lo mantiene al día.
   *
   * Todo sale de atributos que ya estaban en cada radio (`data-price-cents`, `data-quantity`) —
   * no hace falta pedirle nada al servidor.
   */
  #updateSummary() {
    const {
      summary,
      summaryLabel,
      summaryTotal,
      summaryCompare,
      summarySave,
      summaryTransfer,
      summaryShipping,
      summaryTransferShipping,
      summaryInstallments,
      shippingCost,
      shippingFree,
    } = this.refs;

    // El resumen es opcional: un producto sin packs válidos no lo renderiza.
    if (!summary) return;

    const tierInput = this.#getSelectedTierInput();
    if (!tierInput) return;

    const card = tierInput.closest('.purchase-options__tier');
    const money = (cents) => this.#moneyShort(cents);

    const total = Number(tierInput.dataset.priceCents) || 0;
    const qty = Number(tierInput.dataset.quantity) || 1;
    // En centavos desde Liquid, no parseando el texto ya formateado de la tarjeta: en es-AR el
    // punto es separador de miles y la coma es el decimal, así que `parseFloat('278.000,00')`
    // devuelve 278. Cualquier parseo propio tendría además que saber cuántos decimales usa la
    // moneda. El número crudo no tiene ninguno de esos problemas.
    const compare = Number(tierInput.dataset.compareCents) || 0;

    if (summaryLabel) {
      // «3 pack · 3 unidades» o «4 paquetes · 16 huellitas»: lo arma Liquid con el mismo snippet
      // que la tarjeta (`pack-tier-rotulo`), así el resumen no puede decir otra cosa que ella. Sin
      // el atributo (HTML de antes en caché), la redacción de siempre.
      const desc =
        tierInput.dataset.summary || `${this.#tierLabel(card)} · ${qty} ${qty === 1 ? 'unidad' : 'unidades'}`;
      summaryLabel.textContent = `${desc} · Total`;
    }

    if (summaryTotal) summaryTotal.textContent = money(total);

    const hasDiscount = compare > total;
    if (summaryCompare) {
      summaryCompare.textContent = money(compare);
      summaryCompare.hidden = !hasDiscount;
    }
    if (summarySave) {
      summarySave.textContent = `Ahorrás ${money(compare - total)}`;
      summarySave.hidden = !hasDiscount;
    }

    if (summaryTransfer) {
      const pct = Number(summary.dataset.transferPercent) || 0;
      summaryTransfer.textContent = `${money(Math.round((total * (100 - pct)) / 100))} por transferencia`;
      summaryTransfer.hidden = pct <= 0;
    }

    if (summaryInstallments) {
      // `Math.round` y no una división entera: el importe tiene que dar el mismo número que el
      // checkout. $89.000 en 3 son $29.666,67, no $29.666,66 — es la misma razón por la que en
      // Liquid se divide por un float. Con 0 o 1 cuota el renglón no tiene nada que decir.
      const cuotas = Number(summary.dataset.installmentsCount) || 0;
      summaryInstallments.hidden = cuotas < 2;
      if (cuotas >= 2) {
        summaryInstallments.textContent = `${cuotas} cuotas sin interés de ${money(Math.round(total / cuotas))}`;
      }
    }

    if (summaryShipping) {
      // Este tier MÁS lo que ya hay en el carrito — el mismo criterio que aplica Liquid en el
      // primer pintado y que usan los avisos de las tarjetas. Si acá se comparara el tier solo,
      // el resumen podría decir "Envío gratis" mientras la tarjeta de al lado dice "Te faltan $X"
      // sobre exactamente el mismo pack.
      summaryShipping.hidden = !this.#reachesFreeShipping(total);
    }

    if (summaryTransferShipping) {
      // La contracara del chip de arriba: el 10% de transferencia es un descuento de pedido, y la
      // tarifa de $0 mira el total DESPUÉS de descuentos. Con el chip prendido, si el pedido entero
      // (este pack más el carrito) pagado por transferencia cae bajo el umbral, el checkout cobra
      // el envío. Misma cuenta que el primer pintado de `blocks/purchase-options.liquid`.
      const pct = Number(summary.dataset.transferPercent) || 0;
      const threshold = Number(this.dataset.freeShippingThresholdCents) || 0;
      const orderByTransfer = Math.round(((total + this.#cartTotalCents) * (100 - pct)) / 100);
      summaryTransferShipping.hidden = !(pct > 0 && this.#reachesFreeShipping(total) && orderByTransfer < threshold);
    }

    if (shippingCost || shippingFree) {
      // El espejo exacto del chip de acá arriba, y por eso la misma cuenta. Las dos mitades del
      // renglón de envío son contracara una de la otra: con envío gratis se apaga el costo y se
      // enciende la palabra "gratis"; sin él, al revés. Nunca las dos, nunca ninguna — si el
      // costo se fuera solo, la frase quedaría en "Llega en 3 a 7 días hábiles" y se leería
      // truncada.
      const gratis = this.#reachesFreeShipping(total);
      if (shippingCost) shippingCost.hidden = gratis;
      if (shippingFree) shippingFree.hidden = !gratis;
    }

    // La barra flotante muestra el mismo total y tiene que seguirlo. Se avisa por evento en vez
    // de escribirle el DOM desde acá: son dos componentes distintos, y que uno le meta mano al
    // otro por `querySelector` los ata para siempre. Burbujea hasta `document` porque la barra
    // vive fuera de este subárbol.
    this.dispatchEvent(
      new CustomEvent(PACK_TIER_CHANGE, {
        bubbles: true,
        composed: true,
        detail: {
          productId: this.dataset.productId,
          label: this.#tierLabel(card),
          totalFormatted: money(total),
        },
      })
    );
  }

  /**
   * El nombre CORTO del pack («4 pack»), el que va en la barra flotante, en «Con el 4 pack tenés
   * envío gratis» y en lo que se anuncia al lector de pantalla. No es lo que muestra la tarjeta:
   * ésa puede decir «Llevá 8, pagá 6», que en esas frases no se lee (`data-label`, Liquid).
   * @param {Element | null | undefined} card
   */
  #tierLabel(card) {
    const el = /** @type {HTMLElement | null | undefined} */ (card?.querySelector('.purchase-options__tier-label'));
    return el?.dataset.label || el?.textContent.replace(/\s+/g, ' ').trim() || '';
  }

  /**
   * Formatea centavos sin los ",00" cuando no hay centavos reales.
   *
   * Es el equivalente en JS del filtro `money_without_trailing_zeros` que usa Liquid para el
   * primer pintado del resumen: se cambia el placeholder del formato de la tienda por su variante
   * sin decimales. Tiene que dar EXACTAMENTE lo mismo que Liquid o al tocar un pack el número
   * cambiaría de forma además de cambiar de valor, que se lee como un parpadeo.
   *
   * @param {number} cents
   * @returns {string}
   */
  #moneyShort(cents) {
    const format = this.dataset.moneyFormat ?? '{{amount}}';
    const currency = this.dataset.currency ?? 'ARS';
    const whole = cents % 100 === 0;
    // El orden importa: primero la variante con separador de miles (la que usa esta tienda,
    // `${{amount_with_comma_separator}}`), después el `amount` pelado. Al revés, el segundo
    // reemplazo no llega a correr porque `amount_no_decimals_with_comma_separator` ya no tiene
    // un límite de palabra después de "amount".
    const shortFormat = whole
      ? format
          .replace('amount_with_comma_separator', 'amount_no_decimals_with_comma_separator')
          .replace(/\bamount\b(?!_)/, 'amount_no_decimals')
      : format;
    return formatMoney(cents, shortFormat, currency);
  }

  /**
   * ¿Este pack, sumado a lo que ya hay en el carrito, alcanza el envío gratis?
   * @param {number} tierCents
   */
  #reachesFreeShipping(tierCents) {
    if (this.dataset.freeShippingEnabled !== 'true') return false;
    const threshold = Number(this.dataset.freeShippingThresholdCents) || 0;
    if (threshold <= 0) return false;
    return tierCents + this.#cartTotalCents >= threshold;
  }

  /**
   * Los avisos de "Te faltan $X para el envío gratis" de cada tarjeta.
   *
   * Éste era el bug: la cuenta miraba SÓLO el precio del pack, así que con el carrito ya cargado
   * anunciaba una diferencia que no existía. Ahora suma el carrito, y se rehace sin recargar
   * cuando el carrito cambia.
   *
   * El importe se busca DENTRO de cada aviso en vez de emparejar dos listas de refs por índice:
   * el emparejamiento por posición se rompe en silencio en cuanto un tier renderiza uno de los
   * dos nodos y no el otro.
   */
  #updateNudges() {
    const nudges = this.refs.tierNudges ?? [];
    if (!nudges.length) return;

    const enabled = this.dataset.freeShippingEnabled === 'true';
    const threshold = Number(this.dataset.freeShippingThresholdCents) || 0;

    // Cuál es el pack MÁS BARATO que alcanza el envío gratis con el carrito actual. Se recalcula
    // en cada cambio y no se lee de un atributo, porque es justamente lo que el carrito mueve: un
    // pack que no calificaba puede empezar a hacerlo, y entonces el aviso de otra tarjeta tiene
    // que cambiar de FRASE, no sólo de número. Ése era el riesgo de actualizar sólo el importe.
    let solverLabel = '';
    let solverCents = 0;
    if (enabled && threshold > 0) {
      for (const input of this.refs.tierInputs) {
        const cents = Number(input.dataset.priceCents) || 0;
        if (cents + this.#cartTotalCents < threshold) continue;
        if (solverCents > 0 && cents >= solverCents) continue;
        solverCents = cents;
        solverLabel = this.#tierLabel(input.closest('.purchase-options__tier'));
      }
    }

    const solvers = this.refs.tierNudgeSolvers ?? [];
    const gaps = this.refs.tierNudgeGaps ?? [];
    const solverLabels = this.refs.tierNudgeSolverLabels ?? [];

    nudges.forEach((nudge, i) => {
      const tierCents = Number(nudge.dataset.tierCents) || 0;
      const gap = threshold - (tierCents + this.#cartTotalCents);
      const show = enabled && threshold > 0 && gap > 0;

      nudge.hidden = !show;
      if (!show) return;

      // Se emparejan por índice y no buscando dentro del aviso porque los tres refs se emiten
      // SIEMPRE juntos, en el mismo orden y una vez por tier: el índice es estable por
      // construcción. Si alguno faltara, el `?.` deja el resto funcionando.
      const useSolver = solverLabel !== '';
      if (solvers[i]) solvers[i].hidden = !useSolver;
      if (gaps[i]) gaps[i].hidden = useSolver;

      if (useSolver) {
        if (solverLabels[i]) solverLabels[i].textContent = solverLabel;
        return;
      }

      const amount = gaps[i]?.querySelector('.purchase-options__nudge-amount');
      // Con decimales, no `#moneyShort`: este aviso vive dentro de la tarjeta, justo debajo de
      // los precios del pack, y una columna de importes quiere precisión pareja. El resumen es
      // un titular y ahí sí van sin centavos.
      if (amount) {
        amount.textContent = formatMoney(gap, this.dataset.moneyFormat ?? '{{amount}}', this.dataset.currency ?? 'ARS');
      }
    });
  }

  /** Se invoca desde el `on:change` del fieldset de tiers. */
  handleTierChange() {
    this.#updateSummary();
  }

  /**
   * Se invoca desde el `on:change` del fieldset de talles.
   *
   * El talle elegido se lee de los refs, NO de `event.target`. Cuando el atributo `on:` vive en un
   * contenedor y el evento nace en un descendiente, `component.js` entrega el evento envuelto en
   * un Proxy que devuelve **el contenedor** como `target` — así que acá `event.target` es el
   * `<fieldset>`, nunca el `<input>` que se tocó. Una guarda del tipo
   * `event.target instanceof HTMLInputElement` descarta TODOS los eventos y se lee como "el
   * listener no está cableado". Es también por lo que `handleTierChange()` no recibe argumentos.
   */
  handleSizeChange() {
    const input = this.refs.sizeInputs?.find((candidate) => candidate.checked);
    if (!input) return;

    this.#selectedSize = input.value;
    this.#applySize({ announce: true });
    this.#syncVariantParam();
  }

  /**
   * El talle de un regalo que trae eje PROPIO — el caso de un pack sin talles que regala un
   * producto que sí los tiene, donde no hay talle del pack que espejar.
   *
   * Acá `event.target` SÍ es el `<select>`, al revés que en `handleSizeChange()`: el atributo
   * `on:change` vive en el control mismo y no en un contenedor, así que `component.js` no proxea
   * nada. La diferencia entre los dos handlers no es estilística, es esa.
   *
   * No toca la matriz ni repinta precios: un regalo es gratis y no mueve ningún total. Lo único
   * que cambia es qué variante se va a agregar, y eso vive en el `data-*` de su fila, que es de
   * donde lo lee `#buildPackItems()`.
   * @param {Event} event
   */
  handleGiftSizeChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const giftNode = select.closest('.purchase-options__gift-item');
    if (!(giftNode instanceof HTMLElement)) return;

    const value = select.value;
    if (!value) return;

    giftNode.dataset.giftSize = value;

    // La variante sale del mapa de la FILA y no de la opción tocada: el desplegable es el mismo
    // control compartido con el carrito, y no tiene por qué saber que existen las variantes. Mismo
    // patrón que `data-pack-size-map` en la fila del carrito.
    const variantId = parseGiftSizeMap(giftNode.dataset.giftSizeMap).get(value)?.variantId;
    if (variantId) giftNode.dataset.variantId = variantId;

    this.refs.liveRegion.textContent = `${select.getAttribute('aria-label') ?? 'Talle'}: ${value}`;
  }

  /**
   * Repinta TODO lo que depende del talle, sin una sola request: precios de cada tarjeta,
   * tachados, unitarios, badges de ahorro, ids de variante y de regalo, disponibilidad, resumen,
   * avisos de envío gratis y el botón.
   *
   * Reescribe los `data-*` de cada radio antes de llamar a `#updateSummary()`/`#updateNudges()`
   * en vez de tener su propia versión de esas cuentas: esos dos métodos ya leen exactamente esos
   * atributos, así que actualizando la fuente el resumen y los avisos se corrigen solos. Una
   * segunda implementación de la misma aritmética es cómo este tema terminó una vez con dos
   * porcentajes de descuento distintos para el mismo descuento.
   * @param {{announce?: boolean}} [options]
   */
  /**
   * ¿Este producto tiene talles de verdad, o la matriz lleva la entrada única del centinela?
   * `values` vacío es la señal, y no `dataset.hasSizeAxis`, porque la matriz es la fuente de todo
   * lo demás que decide este componente y dos fuentes para el mismo hecho se desincronizan.
   * @returns {boolean}
   */
  #hasSizeAxis() {
    return Array.isArray(this.#matrix?.values) && this.#matrix.values.length > 0;
  }

  /**
   * " en talle M" cuando hay talles, y nada cuando no. Los mensajes se arman con esto en vez de
   * tener dos redacciones: sin talle, "Sin stock" a secas es la frase correcta.
   * @returns {string}
   */
  #sizePhrase() {
    if (!this.#hasSizeAxis()) return '';
    return ` en ${(this.#matrix?.option || 'Talle').toLowerCase()} ${this.#selectedSize}`;
  }

  /**
   * Por qué una tarjeta está apagada. «Sin stock» sólo cuando de verdad no queda nada: un pack de
   * 12 en un talle con 10 paquetes no está «sin stock», le faltan unidades — y decir «Sin stock»
   * ahí espanta al que quería el pack de 4 del mismo talle. Mismo número que el aviso de pocas
   * unidades: el stock que informa Shopify menos lo que el carrito ya se lleva de ese talle.
   * @param {string} size
   * @param {number} reserved
   * @returns {string}
   */
  #unavailablePhrase(size, reserved) {
    const info = this.#matrix?.sizes?.[size];
    if (info?.capped) {
      const remaining = Math.max(0, (Number(info.stock) || 0) - reserved);
      if (remaining > 0) return `Quedan ${remaining}${this.#sizePhrase()}`;
    }
    return `Sin stock${this.#sizePhrase()}`;
  }

  /**
   * Los productos distintos que surten a los regalos de talle propio de esta ficha.
   * @returns {Set<string>}
   */
  #giftStockHandles() {
    /** @type {Set<string>} */
    const handles = new Set();
    for (const gift of this.querySelectorAll('.purchase-options__gift-item[data-gift-stock-handle]')) {
      if (!(gift instanceof HTMLElement)) continue;
      const handle = gift.dataset.giftStockHandle;
      if (handle) handles.add(handle);
    }
    return handles;
  }

  /**
   * Apaga en el desplegable de cada regalo los talles que ya no se pueden llevar, descontando lo que
   * el carrito tiene comprometido de SU producto.
   *
   * Es lo que evita el camino feo: sin esto el cliente podía elegir un talle agotado, el servidor
   * rechazaba el agregado, el cartel de error pasaba en un segundo y el regalo terminaba borrado.
   * El bloqueo llega antes del rechazo.
   *
   * @param {boolean} announce
   */
  #repaintGiftSizes(announce) {
    for (const gift of this.querySelectorAll('.purchase-options__gift-item[data-gift-size]')) {
      if (!(gift instanceof HTMLElement)) continue;

      const dropdown = gift.querySelector('pack-size-dropdown');
      if (!dropdown || typeof (/** @type {any} */ (dropdown).setAvailability) !== 'function') continue;

      const needed = Number(gift.dataset.quantity) || 1;
      const reserved = this.#reservedByGiftHandle.get(gift.dataset.giftStockHandle ?? '') ?? new Map();

      /** @type {Record<string, {available: boolean}>} */
      const states = {};
      const bySize = parseGiftSizeMap(gift.dataset.giftSizeMap);
      let anyAvailable = false;

      for (const [size, info] of bySize) {
        const free = info.capped ? info.stock - (reserved.get(size) ?? 0) : Infinity;
        const available = free >= needed;
        // "sin stock" sólo cuando no queda nada; con unidades libres pero menos que las que el
        // regalo necesita, el aviso dice cuántas hay (misma regla que el desplegable del carrito).
        states[size] = { available, note: free > 1 ? `quedan ${free}` : free === 1 ? 'queda 1' : 'sin stock' };
        if (available) anyAvailable = true;
      }

      if (Object.keys(states).length === 0) continue;

      const jumped = /** @type {any} */ (dropdown).setAvailability(states);
      if (jumped) {
        gift.dataset.giftSize = jumped;
        const variantId = bySize.get(jumped)?.variantId;
        if (variantId) gift.dataset.variantId = variantId;
        if (announce) {
          this.refs.liveRegion.textContent = `Ese talle del regalo se quedó sin stock. Elegimos ${jumped}.`;
        }
      }

      // Ningún talle disponible: el pack se sigue pudiendo comprar y el regalo no viaja
      // (`#buildItems` saltea el marcado sin stock). Lo único que hace falta es decirlo, en vez de
      // que el cliente lo descubra cuando la línea no aparece.
      this.#markGiftSoldOut(gift, !anyAvailable);
    }

    // Los regalos SIN talle (un solo producto, como los Guantes x6 del pack del Pulidor): la misma
    // cuenta con una sola cifra, descontando lo que el carrito ya se lleva de ese producto. Sin esto,
    // agotado el producto que surte al regalo, la ficha lo seguía ofreciendo y el agregado fallaba
    // entero: un regalo-bundle no se descarta en silencio (`addPackItems`).
    for (const gift of this.querySelectorAll('.purchase-options__gift-item[data-gift-stock]')) {
      if (!(gift instanceof HTMLElement)) continue;
      const needed = Number(gift.dataset.quantity) || 1;
      const stock = Number(gift.dataset.giftStock) || 0;
      const reserved = this.#reservedByGiftHandle.get(gift.dataset.giftStockHandle ?? '')?.get(NO_SIZE_KEY) ?? 0;
      this.#markGiftSoldOut(gift, stock - reserved < needed);
    }
  }

  /**
   * @param {HTMLElement} gift
   * @param {boolean} soldOut
   */
  #markGiftSoldOut(gift, soldOut) {
    gift.classList.toggle('purchase-options__gift-item--sold-out', soldOut);
    const note = gift.querySelector('.purchase-options__gift-note');
    // «sin stock» y no «sin stock por ahora»: la tira va en UN renglón y la nota compite con el nombre.
    if (note) note.textContent = soldOut ? 'sin stock' : '';
  }

  #applySize({ announce = false } = {}) {
    if (!this.#matrix) return;

    const size = this.#selectedSize;
    this.classList.toggle('purchase-options--pending', !size);

    const money = (cents) =>
      formatMoney(cents, this.dataset.moneyFormat ?? '{{amount}}', this.dataset.currency ?? 'ARS');
    const reserved = size ? this.#reservedBySize.get(size) ?? 0 : 0;

    let firstAvailableInput = null;
    let checkedBecameUnavailable = false;

    for (const input of this.refs.tierInputs) {
      const card = input.closest('.purchase-options__tier');
      const cell = cellFor(this.#matrix, size, input.value);
      const { available } = availabilityFor(this.#matrix, size, cell, reserved);

      // Sin talle elegido todavía: se conservan los números que pintó Liquid y sólo se atenúa el
      // conjunto. Pintar acá los de un talle que el cliente no eligió sería inventar.
      if (!size) {
        card?.classList.remove('purchase-options__tier--unavailable');
        continue;
      }

      card?.classList.toggle('purchase-options__tier--unavailable', !available);
      if (available && !firstAvailableInput) firstAvailableInput = input;
      if (!available && input.checked) checkedBecameUnavailable = true;
      // `disabled` y no sólo la clase: un radio deshabilitado no se puede marcar ni con teclado
      // ni con un clic que caiga en el borde de la tarjeta.
      input.disabled = !available;

      const unavailableNode = card?.querySelector('.purchase-options__tier-unavailable');
      if (unavailableNode) {
        unavailableNode.textContent = available ? '' : this.#unavailablePhrase(size, reserved);
        unavailableNode.hidden = available;
      }

      if (!cell) continue;

      // La fuente que leen `#updateSummary` y `#updateNudges`.
      input.dataset.variantId = String(cell.id);
      input.dataset.quantity = String(cell.qty);
      input.dataset.priceCents = String(cell.total);
      input.dataset.compareCents = String(cell.compare);

      const totalNode = card?.querySelector('.purchase-options__tier-total');
      if (totalNode) totalNode.textContent = money(cell.total);

      const compareNode = card?.querySelector('.purchase-options__tier-compare');
      if (compareNode) {
        compareNode.textContent = money(cell.compare);
        compareNode.hidden = !(cell.compare > cell.total);
      }

      const unitNode = card?.querySelector('.purchase-options__tier-unit-price');
      if (unitNode) {
        unitNode.textContent = `${money(cell.unit)} c/u`;
        // El unitario se esconde cuando la tarjeta está apagada: ahí manda el motivo, no el
        // precio por unidad de algo que no se puede comprar.
        unitNode.hidden = cell.unit <= 0 || !available;
      }

      const badgeNode = /** @type {HTMLElement | null | undefined} */ (card?.querySelector('.purchase-options__badge'));
      // El badge canta el % real del talle elegido, que puede diferir del pintado por Liquid
      // cuando el compare-at cambia por talle. Salvo el «Y 5% MENOS» de un «Llevá 8, pagá 6»
      // (`data-badge-fijo`): sale del % del pack, igual en todos los talles.
      if (badgeNode && !badgeNode.hasAttribute('data-badge-fijo')) {
        badgeNode.textContent = `AHORRÁ ${cell.savings}%`;
        badgeNode.hidden = !(cell.savings > 0);
      }

      // Los avisos de envío gratis se recalculan sobre el precio del talle elegido.
      const nudge = card?.querySelector('.purchase-options__nudge');
      if (nudge) nudge.dataset.tierCents = String(cell.total);

      // Los regalos siguen al talle del pack. Se emparejan por SLOT y no por posición en el DOM
      // ni por variante: la variante es justamente lo que cambia con el talle.
      for (const giftNode of card?.querySelectorAll('.purchase-options__gift-item[data-gift-slot]') ?? []) {
        // Un regalo de talle PROPIO no se repinta desde acá: su variante la decide su propio
        // selector, y la matriz sólo sabe espejar el talle del pack. Sin esta guarda, cualquier
        // repintado posterior —el que dispara `#refreshReserved` al terminar de leer el carrito,
        // por ejemplo— le devolvía la variante del primer talle y borraba en silencio lo que el
        // cliente acababa de elegir, unos cientos de milisegundos después de elegirlo.
        if (giftNode.dataset.giftSize) continue;

        const slot = Number(giftNode.dataset.giftSlot);
        const gift = cell.gifts?.find((candidate) => candidate.slot === slot);
        if (gift) giftNode.dataset.variantId = String(gift.id);
      }
    }

    this.#repaintSizeButtons();

    // Nunca queda un pack apagado marcado. Se mueve al más cercano por cantidad —no al primero—
    // porque quien eligió el 3 pack quiere el más grande que le entre, no el más chico.
    if (checkedBecameUnavailable) {
      const replacement = this.#nearestAvailableTier();
      if (replacement) {
        replacement.checked = true;
        if (announce) {
          const label = this.#tierLabel(replacement.closest('.purchase-options__tier'));
          this.refs.liveRegion.textContent = `Ese pack no tiene stock${this.#sizePhrase()}. Elegimos ${label}.`;
        }
      } else {
        // Ningún pack entra en este talle. Se desmarca todo en vez de dejar marcado uno que el
        // botón no podría agregar.
        for (const input of this.refs.tierInputs) input.checked = false;
        if (announce) {
          this.refs.liveRegion.textContent = `No queda stock${this.#sizePhrase()}.`;
        }
      }
    }

    // Los regalos de talle propio se repintan acá y no en su propio camino porque `#applySize` es lo
    // que ya corre después de CADA cambio de carrito (`#handleCartChange` → `#refreshReserved`).
    // Colgarlo de otro lado sería un segundo momento que mantener sincronizado.
    this.#repaintGiftSizes(announce);
    this.#updateStockNote(reserved);
    this.#updateStockStatus();
    this.#updateButtonState();
    this.#updateSummary();
    this.#updateNudges();
  }

  /**
   * El pack disponible más cercano por cantidad al que estaba marcado.
   * @returns {HTMLInputElement | null}
   */
  #nearestAvailableTier() {
    const currentQuantity = Number(this.#getSelectedTierInput()?.dataset.quantity) || 1;
    const reserved = this.#selectedSize ? this.#reservedBySize.get(this.#selectedSize) ?? 0 : 0;

    let best = null;
    let bestDistance = Infinity;
    for (const input of this.refs.tierInputs) {
      const cell = cellFor(this.#matrix, this.#selectedSize, input.value);
      if (!availabilityFor(this.#matrix, this.#selectedSize, cell, reserved).available) continue;
      const distance = Math.abs((Number(cell?.qty) || 1) - currentQuantity);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = input;
      }
    }
    return best;
  }

  /**
   * "Quedan N unidades en talle M". Sólo cuando el número es real y está por debajo del umbral
   * del schema — `DESIGN.md` §9: los gatillos de urgencia son reales o no van.
   * @param {number} reserved
   */
  #updateStockNote(reserved) {
    const note = this.refs.stockNote;
    if (!note) return;

    const threshold = Number(this.dataset.lowStockThreshold) || 0;
    const size = this.#selectedSize;
    if (!size || threshold <= 0) {
      note.hidden = true;
      return;
    }

    const info = this.#matrix?.sizes?.[size];
    if (!info?.capped) {
      note.hidden = true;
      return;
    }

    const remaining = Math.max(0, (Number(info.stock) || 0) - reserved);
    const show = remaining > 0 && remaining <= threshold;
    note.hidden = !show;
    if (show) {
      note.textContent = `Quedan ${remaining} ${remaining === 1 ? 'unidad' : 'unidades'}${this.#sizePhrase()}`;
    }
  }

  /**
   * ¿Entra al menos un pack en este talle, descontando lo que el carrito ya se lleva de él?
   *
   * Es la misma cuenta que apaga cada tarjeta en `#applySize`, hecha para el talle entero. La usan
   * el botón de talle y la línea de stock, y tienen que coincidir con las tarjetas: un talle tachado
   * con un pack encendido, o "En Stock" con todas las tarjetas grises, es la contradicción que evita.
   * @param {string} size
   * @returns {boolean}
   */
  #sizeHasAvailableTier(size) {
    const reserved = this.#reservedBySize.get(size) ?? 0;
    return this.refs.tierInputs.some(
      (input) => availabilityFor(this.#matrix, size, cellFor(this.#matrix, size, input.value), reserved).available
    );
  }

  /**
   * Tacha en vivo los talles que el carrito ya agotó, y los destacha si se libera stock. Liquid sólo
   * pinta el primer render, sin saber qué hay en el carrito.
   *
   * El talle MARCADO nunca se deshabilita: el cliente se queda parado en él leyendo "Agotado en
   * talle L", en vez de que la ficha lo mueva sola a un talle que no eligió.
   */
  #repaintSizeButtons() {
    if (!this.#hasSizeAxis()) return;
    const optionName = this.#matrix?.option || 'Talle';

    for (const input of this.refs.sizeInputs ?? []) {
      const soldOut = !this.#sizeHasAvailableTier(input.value);
      input.closest('.purchase-options__size')?.classList.toggle('purchase-options__size--sold-out', soldOut);
      input.disabled = soldOut && !input.checked;
      input.setAttribute('aria-label', `${optionName} ${input.value}${soldOut ? ' — sin stock' : ''}`);
    }
  }

  /**
   * La línea "En Stock · Listo para Enviar" de `snippets/product-feature-badges.liquid`, que vive en
   * otro bloque de la misma sección. Liquid la pinta con `product.available`, que no descuenta el
   * carrito.
   *
   * Con talle elegido habla de ESE talle. Sin elegir, el producto está agotado sólo si no queda
   * ningún talle. Se busca dentro de la sección y no en `document`, para no asumir que la página
   * tiene una sola ficha.
   */
  #updateStockStatus() {
    const status = this.closest('.shopify-section')?.querySelector('[data-stock-status]');
    if (!status || !this.#matrix) return;

    const size = this.#selectedSize;
    const soldOut = size
      ? !this.#sizeHasAvailableTier(size)
      : !(this.refs.sizeInputs ?? []).some((input) => this.#sizeHasAvailableTier(input.value));

    const dot = status.querySelector('.stock-dot');
    dot?.classList.toggle('stock-dot--available', !soldOut);
    dot?.classList.toggle('stock-dot--sold-out', soldOut);

    const inStock = status.querySelector('[data-stock-in]');
    const outOfStock = status.querySelector('[data-stock-out]');
    if (inStock instanceof HTMLElement) inStock.hidden = soldOut;
    if (outOfStock instanceof HTMLElement) {
      outOfStock.hidden = !soldOut;
      // Sin talle elegido `#sizePhrase` diría "en talle null": ahí el agotado es del producto entero.
      outOfStock.textContent = `Agotado${size ? this.#sizePhrase() : ''}`;
    }
  }

  /** El botón pide el talle mientras falte, y vuelve a ser el de siempre cuando ya está. */
  #updateButtonState() {
    if (!this.#matrix) return;

    const { addToCartButton, addToCartButtonText } = this.refs;
    const needsSize = !this.#selectedSize;

    addToCartButton.toggleAttribute('aria-disabled', needsSize);
    addToCartButton.dataset.needsSize = String(needsSize);
    const label = needsSize
      ? `Elegí tu ${(this.#matrix.option || 'Talle').toLowerCase()}`
      : Theme.translations?.add_to_cart || 'Agregar al carrito';
    addToCartButtonText.textContent = label;
    // `#announceAdded` cachea el texto por defecto la primera vez que corre, para restaurarlo
    // después de "Agregado". Si lo cacheara mientras el botón dice "Elegí tu talle", el botón se
    // quedaría con ese texto para siempre después del primer agregado.
    this.#defaultButtonText = label;
  }

  /**
   * Deja el talle elegido en la URL, para que sobreviva a una recarga y se pueda compartir.
   * Liquid lo lee de vuelta por `product.selected_variant`, que es no-blank SÓLO cuando la URL
   * trae `?variant=` — y de ahí sale la única preselección legítima de talle.
   */
  #syncVariantParam() {
    const baseId = this.#selectedSize ? this.#matrix?.sizes?.[this.#selectedSize]?.baseId : null;
    if (!baseId) return;

    const url = new URL(window.location.href);
    if (url.searchParams.get('variant') === String(baseId)) return;
    url.searchParams.set('variant', String(baseId));
    history.replaceState({}, '', url.toString());
  }

  /**
   * Relee el stock real del servidor. Sin polling: el stock de una tienda chica no cambia entre
   * dos parpadeos, y una request por minuto en cada ficha abierta costaría más de lo que corrige.
   * Se pide cuando de verdad puede haber cambiado algo bajo los pies del cliente: al volver a la
   * pestaña después de un rato.
   *
   * `sections/pack-stock.liquid` es la única vía: `/products/<handle>.js` trae `available` pero no
   * las unidades, y sin unidades no se puede decidir si entra un pack de 3.
   *
   * La autoridad final sigue siendo `/cart/add.js`. Esto no evita que el servidor rechace un
   * agregado — evita OFRECER un pack que ya no existe, que es distinto.
   */
  async #refreshStockFromServer() {
    if (!this.#matrix) return;
    const handle = this.dataset.productHandle;
    if (!handle) return;

    try {
      const response = await fetch(`${Theme.routes.root_url ?? '/'}products/${handle}?section_id=pack-stock`.replace('//products', '/products'), {
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) return;

      // La Section Rendering API devuelve la sección envuelta en su `<div id="shopify-section-…">`,
      // así que hay que buscar la isla adentro — parsear la respuesta entera como JSON falla.
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = readSizeMatrix(parsed.body);
      if (!fresh) return;

      this.#matrix = fresh;
      this.#applySize({ announce: false });
    } catch {
      // Un refresco que falla deja el stock que ya se tenía. Apagar packs por un hipo de red
      // sería peor que ofrecer uno que el servidor va a rechazar con un mensaje claro.
    }
  }

  /** Relee del carrito cuántas unidades de cada talle ya están comprometidas. */
  async #refreshReserved() {
    if (!this.#matrix) return;
    const handle = this.dataset.productHandle;
    if (!handle) return;

    try {
      const cart = await fetchCartJson();
      this.#reservedBySize = reservedUnitsBySize(cart, handle);

      // La MISMA lectura sirve para los regalos de talle propio: cada uno sale del inventario de
      // otro producto, y lo que ese producto ya tenga comprometido en el carrito es exactamente lo
      // que faltaba para que el desplegable dejara de ofrecer un talle agotado. Cero requests
      // nuevas — sólo un recuento más sobre el carrito que ya está en la mano.
      this.#reservedByGiftHandle = new Map();
      for (const giftHandle of this.#giftStockHandles()) {
        this.#reservedByGiftHandle.set(giftHandle, reservedUnitsBySize(cart, giftHandle));
      }
    } catch {
      // Un carrito que no se pudo leer deja el último recuento conocido: es preferible una
      // disponibilidad apenas desactualizada —que el servidor va a corregir al agregar— a apagar
      // packs que sí se pueden vender.
      return;
    }

    this.#applySize({ announce: false });
  }

  /**
   * Builds the batch of cart line items for the selected tier: the paid anchor (the BASE
   * variant, quantity N — the pack's price comes from Shopify's automatic discount, see
   * `assets/pack-tiers.js`) and one line per gift. Every line carries the pack's group id, so
   * the cart can lock the gifts' quantity, cascade-remove them with the anchor and adjust them
   * by difference when the stepper crosses a tier.
   * @returns {Array<{id: number, quantity: number, properties?: Record<string, string>}>}
   */
  #buildItems() {
    const tierInput = this.#getSelectedTierInput();
    if (!tierInput) return [];

    const tierCard = tierInput.closest('.purchase-options__tier');
    // La ÚNICA identidad del pack, generada acá al nacer y arrastrada sin cambios por todas las
    // operaciones del carrito (ver pack-tiers.js).
    const groupId = crypto.randomUUID();

    // Con eje de talle la celda de la matriz manda sobre los `data-*` del DOM. Los dos dicen lo
    // mismo —`#applySize` los mantiene sincronizados—, pero la matriz es la fuente y el DOM la
    // copia: si algo llegara a desincronizarlos, cobrar por la copia es cómo se le termina
    // mandando al cliente un talle distinto del que eligió.
    const cell = cellFor(this.#matrix, this.#selectedSize, tierInput.value);
    // Sin eje de talle, el talle NO viaja: la clave de la matriz es un centinela interno y
    // grabarla dejaría en el pedido un "Talle: *" que nadie eligió y que quien arma el paquete
    // leería como un dato real.
    const hasSizes = this.#hasSizeAxis();
    const size = hasSizes ? this.#selectedSize ?? undefined : undefined;

    const gifts = [];
    for (const gift of tierCard?.querySelectorAll('.purchase-options__gift-item[data-variant-id]') ?? []) {
      if (!(gift instanceof HTMLElement)) continue;
      // Un regalo que la ficha ya mostró «sin stock» no viaja: el pack se vende sin él, como
      // dice la nota, en vez de que el servidor rechace el lote entero por un accesorio de $0.
      if (gift.classList.contains('purchase-options__gift-item--sold-out')) continue;
      const slot = Number(gift.dataset.giftSlot);
      const fromMatrix = cell?.gifts?.find((candidate) => candidate.slot === slot);
      // Un regalo de TALLE PROPIO manda sobre la matriz: la matriz sólo sabe espejar el talle del
      // pack, y acá el pack puede no tener talle. Su `data-variant-id` lo mantiene al día su
      // propio `select`, así que es la fuente correcta para esta línea.
      const ownSize = gift.dataset.giftSize || '';
      gifts.push({
        variantId: ownSize || !fromMatrix ? Number(gift.dataset.variantId) : fromMatrix.id,
        quantity: Number(gift.dataset.quantity) || 1,
        isBundle: gift.dataset.isBundle === 'true',
        slot: Number.isFinite(slot) ? slot : undefined,
        // El regalo espeja el talle del pack, salvo que traiga uno propio. Cuando el regalo NO
        // tiene eje, su celda resuelve igual a su variante única y este talle queda como dato del
        // pedido, no como selector de nada.
        size: ownSize || size,
        // De qué producto sale el stock de este regalo. Viaja a la línea para que la ficha y el
        // carrito de ESE producto lo descuenten de su inventario.
        stockHandle: gift.dataset.giftStockHandle || undefined,
      });
    }

    return buildTierItems(
      {
        variantId: cell ? cell.id : Number(tierInput.dataset.variantId),
        quantity: cell ? cell.qty : Number(tierInput.dataset.quantity) || 1,
        baseHandle: this.dataset.productHandle,
        size,
        gifts,
      },
      groupId
    );
  }

  addToCart() {
    if (this.#busy) return;

    // Falta el talle. El botón está HABILITADO a propósito (ver el comentario del marcado): un
    // botón muerto no enseña nada, y lo que el cliente concluye de un toque sin respuesta es que
    // la página está rota. Acá el toque tiene a dónde ir — el foco va a la fila, la fila pulsa
    // una vez, y un lector de pantalla lo escucha.
    if (this.#matrix && !this.#selectedSize) {
      const optionName = (this.#matrix.option || 'Talle').toLowerCase();
      this.refs.liveRegion.textContent = `Elegí un ${optionName} para continuar.`;
      const fieldset = this.refs.sizeFieldset;
      const firstEnabled = this.refs.sizeInputs?.find((input) => !input.disabled);
      firstEnabled?.focus({ preventScroll: true });
      if (fieldset) {
        // Se reinicia la animación quitando y volviendo a poner la clase: sin el reflow del
        // medio, volver a agregarla en el mismo fotograma no la vuelve a correr, y el segundo
        // toque no daría ninguna señal.
        fieldset.classList.remove('purchase-options__sizes--pulse');
        void fieldset.offsetWidth;
        fieldset.classList.add('purchase-options__sizes--pulse');
      }
      return;
    }

    const items = this.#buildItems();
    if (items.length === 0) return;

    this.#busy = true;
    this.refs.addToCartButton.disabled = true;
    this.#hideError();

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferredEventPromise.promise,
      })
    );

    // El cajón del carrito se repinta con el markup que trae la MISMA respuesta del agregado
    // (`sections`), en vez de pedir la sección en una request aparte después: medido, ese
    // `?section_id=` era el hop más caro de todo el agregado. Fuera de la ficha (sin cajón en la
    // página) simplemente no se pide.
    const cartItemsComponent = document.querySelector('cart-items-component');
    const cartSectionId =
      cartItemsComponent instanceof HTMLElement ? cartItemsComponent.dataset.sectionId : undefined;

    addPackItems(items, cartSectionId ? { sections: cartSectionId } : undefined)
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: response.description, errors: response.errors },
            })
          );

          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source: 'purchase-options-component',
                  sourceId: this.dataset.productId || '',
                  itemCount: totalQuantity,
                  productId: this.dataset.productId,
                },
              })
            )
            .catch(deferredEventPromise.reject);

          this.#showError(response.message || 'No se pudo agregar al carrito');
          return;
        }

        // Todo lo que sigue ocurre DESPUÉS de que el agregado ya salió bien, así que nada de
        // acá puede volver a contarse como un fallo del agregado. Antes iba suelto dentro del
        // `.then`, de modo que cualquier tropiezo posterior caía en el `.catch` de abajo y le
        // mostraba al cliente "No se pudo agregar al carrito" con el pack YA en el carrito —
        // un error falso, y una venta que se pierde por nada.
        //
        // No es hipotético: `createCartFromAjaxResponse` es del bundle de la plataforma (no
        // nuestro) y ya tiró "Currency code is required with currency style" en esta tienda,
        // y la relectura del carrito puede fallar por un simple hipo de red. En
        // `component-cart-items.js` estas mismas llamadas están envueltas por este motivo
        // desde hace tiempo; acá había quedado sin envolver.
        try {
          const cart = await this.#refreshCart();
          deferredEventPromise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              items: cart.items,
              // El carrito ya leído viaja en el evento: los que escuchan no vuelven a pedirlo.
              cartData: cart,
              // Con el markup acá, `component-cart-items.js` morfea el cajón sin pedir nada.
              sections: response.sections,
              source: 'purchase-options-component',
              sourceId: this.dataset.productId || '',
              itemCount: totalQuantity,
              productId: this.dataset.productId,
              didError: false,
            },
          });
        } catch (error) {
          // Los que escuchan este evento (el contador del header, los recomendados) se quedan
          // sin su actualización, y se corrigen solos en la próxima acción del carrito. El
          // cliente no tiene por qué enterarse de nada: lo suyo funcionó.
          console.error('[purchase-options] El agregado salió bien pero falló la actualización posterior:', error);
          deferredEventPromise.reject(error);
        }

        this.#announceAdded();
      })
      .catch((error) => {
        console.error(error);
        deferredEventPromise.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
        this.#showError('No se pudo agregar al carrito');
      })
      .finally(() => {
        this.#busy = false;
        this.refs.addToCartButton.disabled = false;
      });
  }

  #announceAdded() {
    const { addToCartButtonText, liveRegion } = this.refs;

    if (this.#defaultButtonText === undefined) {
      this.#defaultButtonText = addToCartButtonText.textContent ?? '';
    }

    liveRegion.textContent = Theme.translations.added || 'Agregado';
    addToCartButtonText.textContent = Theme.translations.added || 'Agregado';

    clearTimeout(this.#addedTimeout);
    this.#addedTimeout = setTimeout(() => {
      addToCartButtonText.textContent = this.#defaultButtonText ?? '';
    }, ADDED_BUTTON_TEXT_DURATION);

    setTimeout(() => {
      if (liveRegion.textContent === (Theme.translations.added || 'Agregado')) liveRegion.textContent = '';
    }, SUCCESS_MESSAGE_DISPLAY_DURATION);
  }

  /** @param {string} message */
  #showError(message) {
    const { addToCartError, liveRegion } = this.refs;

    liveRegion.textContent = message;

    if (!addToCartError) return;

    addToCartError.textContent = message;
    addToCartError.classList.remove('hidden');

    clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => {
      addToCartError.classList.add('hidden');
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  #hideError() {
    const { addToCartError } = this.refs;
    if (!addToCartError) return;
    addToCartError.classList.add('hidden');
  }
}

if (!customElements.get('purchase-options-component')) {
  customElements.define('purchase-options-component', PurchaseOptionsComponent);
}
